import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ProfileDropdown from 'Components/Common/ProfileDropdown';
import LightDark from 'Components/Common/LightDark';

import { changeSidebarVisibility } from 'slices/thunks';
import { useSelector, useDispatch } from 'react-redux';
import { createSelector } from 'reselect';
import POS2ULogo from 'assets/pos2u/logo/logo_pos2u.png';
import { RootState } from 'slices';
import { getMerchant, getMyAccount } from 'services/authService';
import {
  setBranches,
  setBranchCodes,
  setIsBranchesLoaded,
  setIsMerchantLoaded,
  setIsUserLoaded,
  setMerchant,
  setUser,
  setStoresInfo,
  setStoreName,
  setIsUserLoading,
  setStoreType,
} from 'slices/info/reducer';
import { getStoresInfo } from 'services/storeService';
import {
  convertStoresInfoToBrachCode,
  convertStoresInfoToStoresText,
  convertToStoresInfoToBranchOptions2,
  getStoreNameByID,
} from 'utils/stores/converter';
import {
  setPageConfig,
  setPageFiltersBranch,
} from 'slices/product/form/reducer';
import { setStoresText, setStoresTextMap } from 'slices/exportData/reducer';
import { setIsLoadMenuSuccess, setRawMenu } from 'slices/common/menu/reducer';

const Header = ({ onChangeLayoutMode, layoutModeType, headerClass }: any) => {
  const dispatch: any = useDispatch();
  const location = useLocation();
  const history = useNavigate();

  const uri = location.pathname;

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const dev = query.get('dev');

  const navHeight = useMemo(() => '48px', []);

  const selectDashboardData = createSelector(
    (state) => state.Layout,
    (sidebarVisibilitytype) => sidebarVisibilitytype.sidebarVisibilitytype
  );

  const sidebarVisibilitytype = useSelector(selectDashboardData);

  const infoState = useSelector((state: RootState) => state.Info);
  const {
    merchant,
    isMerchantLoaded,
    user,
    branches,
    StoresInfo,
    storeName,
    isUserLoaded,
  } = infoState;

  const fetchMerchantData = useCallback(async () => {
    const res = await getMerchant();
    if (res) {
      const { message, error_code, data } = res;
      if (message === 'OK' && error_code === 0) {
        dispatch(setMerchant(data));
        const {
          group_price,
          custom_item_code,
          default_tax_1_rate,
          store_type,
        } = data;
        dispatch(
          setPageConfig({ group_price, custom_item_code, default_tax_1_rate })
        );
        dispatch(setStoreType(store_type));
      } else {
        dispatch(setMerchant({}));
        dispatch(setStoreType(null));
      }
    } else {
      console.log('No response from get Merchant');
    }
    dispatch(setIsMerchantLoaded(true));
  }, [dispatch]);

  const hasFetchedUserData = useRef(false);

  const fetchUserData = useCallback(async () => {
    dispatch(setIsUserLoading(true));
    dispatch(setIsLoadMenuSuccess(true));
    try {
      const res = await getMyAccount();
      if (res) {
        const {
          username,
          expire_date,
          store_id_login,
          permitted_link_list,
          menu_data,
          permissions_detail,
        } = res;
        if (username && expire_date && store_id_login && permitted_link_list) {
          const userinfo = {
            username,
            expire_date,
            store_id_login,
            permitted_link_list,
            permissions_detail,
          };
          dispatch(setUser(userinfo));
        } else {
          dispatch(setUser({}));
        }
        if (menu_data) {
          dispatch(setRawMenu(menu_data));
        } else {
          dispatch(setRawMenu([]));
        }
      } else {
        console.log('No response from getMyAccount');
      }
    } catch (error) {
      console.error('Failed to fetch my account:', error);
    } finally {
      dispatch(setIsUserLoading(false));
      dispatch(setIsUserLoaded(true));
      dispatch(setIsLoadMenuSuccess(true));
    }
  }, [dispatch]);

  const fetchStoresInfo = useCallback(async () => {
    try {
      const rs = await getStoresInfo();

      if (rs) {
        const { stores } = rs;
        const BranchOpt = await convertToStoresInfoToBranchOptions2(stores);
        const BranchCodes = await convertStoresInfoToBrachCode(stores);
        const StoresText = await convertStoresInfoToStoresText(stores);
        const storeTextMap: Record<string, string> = stores.reduce(
          (acc, store) => {
            acc[store.id.toString()] = `${store.code}:${store.name}`;
            return acc;
          },
          {} as Record<string, string>
        );

        dispatch(setStoresInfo(stores));
        dispatch(setBranches(BranchOpt));
        dispatch(setBranchCodes(BranchCodes));
        dispatch(setStoresText(StoresText));
        dispatch(setStoresTextMap(storeTextMap));
      } else {
        dispatch(setStoresInfo([]));
        dispatch(setBranches([]));
        dispatch(setBranchCodes([]));
        dispatch(setStoresText([]));
        dispatch(setStoresTextMap({}));
      }
      dispatch(setIsBranchesLoaded(true));
    } catch (error) {
      console.error('Failed to fetch stores info:', error);
    }
  }, [dispatch]);

  useEffect(() => {
    if (branches.length === 0) {
      fetchStoresInfo();
    } else {
      dispatch(setIsBranchesLoaded(true));
    }
  }, [branches, fetchStoresInfo, dispatch]);
  useEffect(() => {
    if (user.store_id_login) {
      dispatch(setPageFiltersBranch([user.store_id_login.toString()]));
    }
  }, [user.store_id_login, dispatch]);

  useEffect(() => {
    if (Object.keys(merchant).length === 0) {
      fetchMerchantData();
    } else {
      dispatch(setIsMerchantLoaded(true));
    }
  }, [merchant, fetchMerchantData, dispatch]);

  useEffect(() => {
    if (!hasFetchedUserData.current && !isUserLoaded) {
      hasFetchedUserData.current = true;
      fetchUserData();
    }
  }, [fetchUserData, isUserLoaded]);

  useEffect(() => {
    if (user.store_id_login && StoresInfo.length > 0) {
      const name = getStoreNameByID(StoresInfo, user.store_id_login);
      if (name) {
        dispatch(setStoreName(name));
      } else {
        dispatch(setStoreName(''));
      }
    }
  }, [StoresInfo, dispatch, user.store_id_login]);

  useEffect(() => {
    if (uri.startsWith('/no-access')) {
      return;
    }

    if (
      Array.isArray(user.permitted_link_list) &&
      !user.permitted_link_list.some((permitted) =>
        uri.startsWith(permitted)
      ) &&
      dev !== 'pos2u_dev'
    ) {
      history(`/no-access${uri}`);
    }
  }, [uri, user.permitted_link_list, history, dev]);

  const [currentWindowWidth, setCurrentWindowWidth] = useState<number>(
    window.innerWidth
  );

  const trigMarginRight = currentWindowWidth < 1025 ? '' : '-10%';
  const trigMarginRight2 = currentWindowWidth < 1025 ? '' : '-20%';
  const LogoMarginLeft = currentWindowWidth < 767 ? '' : '7.5%';

  useEffect(() => {
    const handleResize = () => {
      setCurrentWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toogleMenuBtn = () => {
    const windowSize = document.documentElement.clientWidth;
    const humberIcon = document.querySelector('.hamburger-icon') as HTMLElement;
    dispatch(changeSidebarVisibility('show'));

    if (windowSize > 767) humberIcon.classList.toggle('open');

    //For collapse horizontal menu
    if (document.documentElement.getAttribute('data-layout') === 'horizontal') {
      if (document.body.classList.contains('menu')) {
        document.body.classList.remove('menu');
      } else {
        document.body.classList.add('menu');
      }
    }

    //For collapse vertical and semibox menu
    if (
      sidebarVisibilitytype === 'show' &&
      (document.documentElement.getAttribute('data-layout') === 'vertical' ||
        document.documentElement.getAttribute('data-layout') === 'semibox')
    ) {
      if (windowSize < 1025 && windowSize > 767) {
        document.body.classList.remove('vertical-sidebar-enable');

        // document.documentElement.getAttribute('data-sidebar-size') === 'sm'
        //   ? document.documentElement.setAttribute('data-sidebar-size', '')
        //   : document.documentElement.setAttribute('data-sidebar-size', 'sm');
        if (
          document.documentElement.getAttribute('data-sidebar-size') === 'sm'
        ) {
          document.documentElement.setAttribute('data-sidebar-size', '');
        } else {
          document.documentElement.setAttribute('data-sidebar-size', 'sm');
        }
      } else if (windowSize > 1025) {
        document.body.classList.remove('vertical-sidebar-enable');

        if (
          document.documentElement.getAttribute('data-sidebar-size') === 'lg'
        ) {
          document.documentElement.setAttribute('data-sidebar-size', 'sm');
        } else {
          document.documentElement.setAttribute('data-sidebar-size', 'lg');
        }
      } else if (windowSize <= 767) {
        document.body.classList.add('vertical-sidebar-enable');
        document.documentElement.setAttribute('data-sidebar-size', 'lg');
      }
    }

    //Two column menu
    if (document.documentElement.getAttribute('data-layout') === 'twocolumn') {
      if (document.body.classList.contains('twocolumn-panel')) {
        document.body.classList.remove('twocolumn-panel');
      } else {
        document.body.classList.add('twocolumn-panel');
      }
    }
  };

  return (
    <React.Fragment>
      <header
        id="page-topbar"
        className={headerClass}
        style={{ height: navHeight }}
      >
        <div
          className="layout-width px-0 mx-0 my-0 py-0"
          style={{ height: navHeight }}
        >
          <div
            className="navbar-header px-0 mx-0 my-0 py-0"
            // className="d-flex px-0 mx-0 my-0 py-0"
            style={{ height: navHeight }}
          >
            <div
              className="d-flex my-0 py-0"
              style={{ height: navHeight, marginLeft: LogoMarginLeft }}
            >
              <div
                className="navbar-brand-box horizontal-logo px-0 mx-0 my-0 py-0"
                style={{ height: navHeight }}
              >
                <Link
                  to="/"
                  className="logo logo-dark px-0 mx-0 my-0 py-0"
                  style={{ height: navHeight }}
                >
                  <span
                    className="logo-lg d-flex align-item-center"
                    style={{
                      height: navHeight,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <img src={POS2ULogo} alt="" height="34" />
                  </span>
                </Link>

                <Link
                  to="/"
                  className="logo logo-light px-0 mx-0 my-0 py-0"
                  style={{ height: navHeight }}
                >
                  <span
                    className="logo-lg"
                    style={{
                      height: navHeight,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <img src={POS2ULogo} alt="" height="34" />
                  </span>
                </Link>
              </div>

              <button
                onClick={toogleMenuBtn}
                type="button"
                className="btn btn-sm px-3 fs-16 header-item vertical-menu-btn topnav-hamburger"
                id="topnav-hamburger-icon"
                style={{ height: navHeight }}
              >
                <span className="hamburger-icon">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </button>

              <div
                className="header-item"
                style={{
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  height: navHeight,
                }}
              >
                <span className="d-flex align-items-center">
                  {isMerchantLoaded ? merchant.company : ''}{' '}
                  {storeName ? storeName : ''}
                </span>
              </div>
            </div>

            <div style={{ height: navHeight, marginRight: trigMarginRight }}>
              {dev === '1' ? (
                <LightDark
                  layoutMode={layoutModeType}
                  onChangeLayoutMode={onChangeLayoutMode}
                />
              ) : null}

              <ProfileDropdown
                navHeight={navHeight}
                trigMarginRight={trigMarginRight2}
              />
            </div>
          </div>
        </div>
      </header>
    </React.Fragment>
  );
};

export default Header;
